import { baseApi } from './baseApi';
import { AMC } from './amcApi';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  jwtToken: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  searchTerm: string;
}

interface PaymentListResponse {
  data: any;
  meta: PaginationMeta;
}

interface PaymentListParams {
  page: number;
  limit: number;
  search?: string;
  amcId?: string;
  status?: string;
  gateway?: string;
  trtype?: string;
}

export interface Asset {
  id: string;
  name: string;
  apiKey: string;
  insertDtr?: boolean;
  insertAotm?: boolean;
  webhookUrl?: string;
  callbackSuccess?: string;
  callbackFailure?: string;
  proxyUrl?: string;
  amc: { id: string };
  upiAutopayPendingHtml?: string;
  allowedIps?: string;
  isActive?: boolean;
}

export interface MerchantGatewayConfig {
  id?: string;
  merchantId: string;
  checksumKey?: string;
  amc: { id: string };
  aggregator: 'Billdesk1' | 'Billdesk2' | 'CamsPay' | 'RazorPay' | 'ePay';
  bankId?: string;
  clientId?: string;
}

export interface MerchantConfigWithFrequency extends MerchantGatewayConfig {
  frequency?: number;
  priority?: number;
}

export interface PaymentConfig {
  id: string;
  name: string;
  trtype: 'LUMPSUM' | 'SIP';
  paymode: 'NET_BANKING' | 'UPI' | 'DEBIT_CARD';
  authmode: 'NET_BANKING' | 'API';
  asset: { id: string };
  merchantConfigs: MerchantConfigWithFrequency[];
  gatewaySelectionAlgo?: 'Priority' | 'Percentage';
}

export interface PaymentLog {
  id: string;
  traceId: string;
  ihNoOrCart: number;
  title: string;
  description: string;
  status: string;
  ip: string | null;
  closure: any | null;
  payload: any | null;
  endpoint: string;
  createdAt: string;
}

export interface PaymentDetails {
  id: string;
  status: string;
  gatewayClicked: string;
  ihNoOrCart: number;
  gateway: string;
  fund: string;
  createdAt: string;
  updatedAt: string;
  reqJson: string;
}

export interface PaymentDetailsResponse {
  paymentDetails: PaymentDetails;
  paymentLogs: PaymentLog[];
}

export interface PaymentLogsResponse {
  logs: PaymentLog[];
}

export interface PaymentTypeBreakdownRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
}

export interface PaymentTypeBreakdownResponse {
  UPICount: number;
  NetBankingCount: number;
  DebitCardCount: number;
  EnachCount: number;
  UAutoCount: number;
  UPIAmount: number;
  NetBankingAmount: number;
  DebitCardAmount: number;
  EnachAmount: number;
  UAutoAmount: number;
}

export interface AggregatorTypeBreakdownResponse {
  Billdesk1Count: number;
  Billdesk2Count: number;
  CamsPayCount: number;
  RazorPayCount: number;
  EPayCount: number;
  Billdesk1Amount: number;
  Billdesk2Amount: number;
  CamsPayAmount: number;
  RazorPayAmount: number;
  EPayAmount: number;
}

export interface AggregatorTypeBreakdownRequest {
  date: string;
  type?: 'weekly' | 'monthly' | 'daily';
}

export interface TransactionCountStatusRequest {
  type?: '1W' | '1M' | '6M' | '1Y' | 'All';
}

export interface TransactionCountStatus {
  Year?: string;
  Month?: string;
  MonthName?: string;
  TransactionDate?: string;
  SuccessCount: number;
  FailureCount: number;
  PendingCount: number;
}

export type TransactionCountStatusResponse = TransactionCountStatus[];

export interface TransactionCountPaymodeRequest {
  type?: '1W' | '1M' | '6M' | '1Y' | 'All';
  gateway?: string;
}

export interface TransactionCountPaymode {
  Year?: string;
  Month?: string;
  MonthName?: string;
  TransactionDate?: string;
  UPISuccessCount: number;
  NetBankingSuccessCount: number;
  DebitCardSuccessCount: number;
  EnachSuccessCount: number;
  UAutoSuccessCount: number;
  TotalSuccessCount: number;
}

export type TransactionCountPaymodeResponse = TransactionCountPaymode[];

interface PaymentSuccessRateComparisonResponse {
  currentWeek: WeeklyPaymentMetrics;
  previousWeek: WeeklyPaymentMetrics;
  changes: PaymentMetricChanges;
}

interface WeeklyPaymentMetrics {
  TimePeriod: string;
  TotalTransactions: number;
  SuccessfulTransactions: number;
  SuccessRate: number;
  ProcessedAmount: number;
}

interface PaymentMetricChanges {
  TotalTransactionsChange: number;
  SuccessfulTransactionsChange: number;
  SuccessRateChange: number;
  ProcessedAmountChange: number;
}

export type SwiftFlowErrorType = 'Recoverable' | 'NonRecoverable' | 'None';

export interface SwiftFlowError {
  id: string;
  originalErrorMessage: string;
  swiftFlowErrorCode: string;
  gateway?: string;
  swiftFlowUnifiedMessage: string;
  errorType?: SwiftFlowErrorType;
  notify?: boolean;
}

export interface SwiftFlowErrorsResponseMeta {
  total: number;
}

export interface SwiftFlowErrorsResponse {
  data: SwiftFlowError[];
  meta: SwiftFlowErrorsResponseMeta;
}

export interface CreateSwiftFlowErrorRequest {
  originalErrorMessage: string;
  swiftFlowErrorCode: string;
  gateway?: string;
  swiftFlowUnifiedMessage: string;
  errorType?: SwiftFlowErrorType;
  notify?: boolean;
}

export const api = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),

    // AMC endpoints
    getAMCs: builder.query<AMC[], void>({
      query: () => '/amcs',
      providesTags: ['AMC'],
    }),

    getAMC: builder.query<AMC, string>({
      query: (id) => `/amcs/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'AMC', id }],
    }),
    createAMC: builder.mutation<AMC, Omit<AMC, 'id'>>({
      query: (amc) => ({
        url: '/amcs',
        method: 'POST',
        body: amc,
      }),
      invalidatesTags: ['AMC'],
    }),

    updateAMC: builder.mutation<AMC, Partial<AMC> & Pick<AMC, 'id'>>({
      query: ({ id, ...patch }) => ({
        url: `/amcs/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'AMC', id }, 'AMC'],
    }),
    deleteAMC: builder.mutation<void, string>({
      query: (id) => ({
        url: `/amcs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'AMC', id }],
    }),

    // Asset endpoints
    getAssets: builder.query<Asset[], string>({
      query: (amcId) => `/assets/amc/${amcId}`,
      providesTags: (_result, _err, amcId) => [{ type: 'Asset', id: amcId }],
    }),

    createAsset: builder.mutation<Asset, Omit<Asset, 'id'>>({
      query: (asset) => ({
        url: `/assets`,
        method: 'POST',
        body: asset,
      }),
      invalidatesTags: (_result, _err, { amc }) => [{ type: 'Asset', id: amc.id }],
    }),

    getPaymentList: builder.query<PaymentListResponse, PaymentListParams>({
      query: (params) => {
        return {
          url: '/payments/',
          params: {
            page: params.page,
            limit: params.limit,
            search: params.search,
            amcId: params.amcId,
            // status: params.status,
            gateway: params.gateway,
            trtype: params.trtype,
          },
        };
      },
      providesTags: ['Payment'],
    }),

    getPayment: builder.query<PaymentDetails, string>({
      query: (id) => `/payments/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Payment', id }],
    }),

    updateAsset: builder.mutation<Asset, Partial<Asset> & Pick<Asset, 'id' | 'amc'>>({
      query: ({ id, amc, ...patch }) => ({
        url: `/assets/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id, amc }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: amc.id },
      ],
    }),

    deleteAsset: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'Asset', id }],
    }),

    // Merchant Gateway Config endpoints
    getMerchantConfigs: builder.query<MerchantGatewayConfig[], void>({
      query: () => '/merchant-configs',
      providesTags: ['MerchantConfig'],
    }),
    getMerchantConfigsByAMC: builder.query<MerchantGatewayConfig[], string>({
      query: (amcId) => `/merchant-configs/amc/${amcId}`,
      providesTags: ['MerchantConfig'],
    }),
    createMerchantConfig: builder.mutation<
      MerchantGatewayConfig,
      Omit<MerchantGatewayConfig, 'id'>
    >({
      query: (config) => ({
        url: '/merchant-configs',
        method: 'POST',
        body: config,
      }),
      invalidatesTags: ['MerchantConfig'],
    }),
    updateMerchantConfig: builder.mutation<
      MerchantGatewayConfig,
      Partial<MerchantGatewayConfig> & Pick<MerchantGatewayConfig, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `/merchant-configs/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        { type: 'MerchantConfig', id },
        'MerchantConfig',
      ],
    }),
    deleteMerchantConfig: builder.mutation<void, string>({
      query: (id) => ({
        url: `/merchant-configs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'MerchantConfig', id }],
    }),
    // Payment Config endpoints
    getPaymentConfigs: builder.query<PaymentConfig[], void>({
      query: () => '/pay-config',
      providesTags: ['PaymentConfig'],
    }),

    getPaymentConfigsByAsset: builder.query<PaymentConfig[], string>({
      query: (assetId) => `/pay-config/asset/${assetId}`,
      providesTags: (_result, _err, assetId) => [{ type: 'PaymentConfig', id: assetId }],
    }),

    getPaymentConfig: builder.query<PaymentConfig, string>({
      query: (id) => `/pay-config/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'PaymentConfig', id }],
    }),

    createPaymentConfig: builder.mutation<PaymentConfig, Omit<PaymentConfig, 'id'>>({
      query: (config) => ({
        url: '/pay-config',
        method: 'POST',
        body: config,
      }),
      invalidatesTags: ['PaymentConfig'],
    }),

    updatePaymentConfig: builder.mutation<
      PaymentConfig,
      Partial<PaymentConfig> & Pick<PaymentConfig, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `/pay-config/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'PaymentConfig', id }],
    }),

    deletePaymentConfig: builder.mutation<void, string>({
      query: (id) => ({
        url: `/pay-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentConfig'],
    }),

    getPaymentById: builder.query<PaymentDetailsResponse, string>({
      query: (id) => `/payments/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'PaymentDetailsResponse', id }],
    }),

    getPaymentLogsById: builder.query<PaymentLogsResponse, string>({
      query: (id) => `/payments/logs/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'PaymentLogsResponse', id }],
    }),

    getPaymentTypeBreakdown: builder.query<
      PaymentTypeBreakdownResponse,
      PaymentTypeBreakdownRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/payments/payment-type-breakdown`,
        params: {
          date: params.date,
          type: params.type,
        },
      }),
      providesTags: ['PaymentTypeBreakdownResponse'],
      transformResponse: (response: any) => {
        return {
          ...response,
          NetBankingAmount: response.NetBankingAmount || 0,
          DebitCardAmount: response.DebitCardAmount || 0,
          EnachAmount: response.EnachAmount || 0,
          UAutoAmount: response.UAutoAmount || 0,
          UPIAmount: response.UPIAmount || 0,
        };
      },
    }),

    getAggregatorTypeBreakdown: builder.query<
      AggregatorTypeBreakdownResponse,
      AggregatorTypeBreakdownRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/payments/aggregator-type-breakdown`,
        params: {
          date: params.date,
          type: params.type,
        },
      }),
      providesTags: ['AggregatorTypeBreakdownResponse'],
      transformResponse: (response: any) => {
        return {
          ...response,
          Billdesk1Amount: response.Billdesk1Amount || 0,
          Billdesk2Amount: response.Billdesk2Amount || 0,
          CamsPayAmount: response.CamsPayAmount || 0,
          RazorPayAmount: response.RazorPayAmount || 0,
          EPayAmount: response.EPayAmount || 0,
        };
      },
    }),

    getTransactionCountStatus: builder.query<
      TransactionCountStatusResponse,
      TransactionCountStatusRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/payments/transaction-count-status`,
        params: {
          type: params.type,
        },
      }),
      providesTags: ['TransactionCountStatusResponse'],
    }),

    getTransactionCountPaymode: builder.query<
      TransactionCountPaymodeResponse,
      TransactionCountPaymodeRequest
    >({
      query: (params) => ({
        method: 'GET',
        url: `/payments/transaction-count-paymode`,
        params: {
          type: params.type,
          gateway: params.gateway,
        },
      }),
      providesTags: ['TransactionCountPaymodeResponse'],
    }),

    getPaymentSuccessRateComparison: builder.query<PaymentSuccessRateComparisonResponse, void>({
      query: () => ({
        method: 'GET',
        url: `/payments/weekly-comparison`,
      }),
      providesTags: ['PaymentSuccessRateComparisonResponse'],
    }),

    // Test payment - creates a sample transaction and returns a URL
    testPayment: builder.mutation<
      { url: string },
      {
        assetId: string;
        trType: string;
        payMode: string;
        authMode: string;
        amount: number;
        // optional bank overrides
        accountNumber?: string;
        ifscCode?: string;
        bankName?: string;
        upi?: string;
        upiMode?: string;
      }
    >({
      query: (body) => ({
        url: `validate/test-payment`,
        method: 'POST',
        body,
      }),
    }),



    // Swift Flow Errors
    createSwiftFlowError: builder.mutation<SwiftFlowError, CreateSwiftFlowErrorRequest>({
      query: (body) => ({
        method: 'POST',
        url: `/swift-flow-errors`,
        body,
      }),
      invalidatesTags: ['SwiftFlowError'],
    }),

    getSwiftFlowErrors: builder.query<SwiftFlowErrorsResponse, void>({
      query: () => ({
        method: 'GET',
        url: `/swift-flow-errors`,
      }),
      providesTags: ['SwiftFlowError'],
    }),

    updateSwiftFlowError: builder.mutation<
      SwiftFlowError,
      { id: string; payload: Pick<SwiftFlowError, 'swiftFlowUnifiedMessage' | 'errorType' | 'notify'> }
    >({
      query: ({ id, payload }) => ({
        method: 'PUT',
        url: `/swift-flow-errors/${id}`,
        body: payload,
      }),
      invalidatesTags: ['SwiftFlowError'],
    }),
  }),
});

export function removeNullOrUndefined(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null && value !== undefined)
  );
}

export const {
  useLoginMutation,
  useGetAMCsQuery,
  useGetAMCQuery,
  useCreateAMCMutation,
  useUpdateAMCMutation,
  useDeleteAMCMutation,
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useGetMerchantConfigsQuery,
  useGetMerchantConfigsByAMCQuery,
  useCreateMerchantConfigMutation,
  useUpdateMerchantConfigMutation,
  useDeleteMerchantConfigMutation,
  useGetPaymentConfigsQuery,
  useGetPaymentConfigsByAssetQuery,
  useGetPaymentConfigQuery,
  useCreatePaymentConfigMutation,
  useUpdatePaymentConfigMutation,
  useDeletePaymentConfigMutation,
  useGetPaymentListQuery,
  useGetPaymentLogsByIdQuery,
  useGetPaymentQuery,
  useGetPaymentByIdQuery,
  useGetPaymentTypeBreakdownQuery,
  useGetAggregatorTypeBreakdownQuery,
  useGetTransactionCountStatusQuery,
  useGetTransactionCountPaymodeQuery,
  useGetPaymentSuccessRateComparisonQuery,
  useTestPaymentMutation,
  useCreateSwiftFlowErrorMutation,
  useGetSwiftFlowErrorsQuery,
  useUpdateSwiftFlowErrorMutation,
} = api;

